import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';

import Button from '../../ui/Button';
import PanelCard from '../../../pages/performance-operations/components/PanelCard';
import { KpiSub, KpiValue } from '../../../pages/performance-operations/styles';
import {
  formatNum, formatSigned, metricSub, metricText,
} from './evaluationAnalytics';

// PM Analitik P4 — E05/E06/E07'nin ortak görsel parçaları: KPI kartı (değer + n/kapsama ya da gizlilik notu), kural tabanlı
// bulgu paneli (analiz §26: sayıyı kural motoru üretir, metin çeviri anahtarıdır, LLM yok), ekran süzgeç satırı ve gizli hücre.
// Çeviriler çağıranın ad alanından (`ns`: performance.competencyGaps | performance.reviewGaps | performance.raterBehavior).

export const ScreenFilters = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
  margin-bottom: 16px;
`;

export const ScreenField = styled.div`
  width: ${({ $w }) => $w || '220px'};
  max-width: 100%;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

export const Note = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.muted};
  margin-bottom: 8px;
  line-height: 1.4;
`;

const HiddenWrap = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
`;

/** Gizli (k altı) hücre: değer yerine kilit + "Gizli"; ipucu gizlilik kuralını açıklar (değer hiçbir yolda gösterilmez). */
export function Hidden({ ns }) {
  const { t } = useTranslation();
  return (
    <HiddenWrap title={t(`${ns}.suppressedNote`)} data-suppressed="1">
      <Lock size={12} aria-hidden="true" />
      {t(`${ns}.suppressed`)}
    </HiddenWrap>
  );
}

/**
 * KPI kartı. `signed`: işaretli fark metriği (+/− öneki; ör. M18, M25). Gizli/veri yok metni metricText'ten (değer asla yazılmaz).
 */
export function EvalKpi({
  ns, code, title, metric, loading, error, onRetry, sub, span = 3, signed = false,
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const hasNumber = metric && !metric.suppressionReason && metric.value !== null && metric.value !== undefined;
  const text = signed && hasNumber ? formatSigned(metric.value, lang) : metricText(t, ns, metric, lang);
  return (
    <PanelCard panelKey={`kpi-${code}`} title={title} span={span} kpi compact loading={loading} error={error} onRetry={onRetry}>
      <KpiValue data-kpi={code}>{text}</KpiValue>
      <KpiSub>{sub !== undefined ? sub : metricSub(t, ns, metric, i18n.language)}</KpiSub>
    </PanelCard>
  );
}

const FindingCard = styled.div`
  padding: 10px 12px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.bgColor};
  border-left: 3px solid ${({ theme, $severity }) => ($severity === 'red' ? theme.colors.auxiliar.danger
    : $severity === 'amber' ? theme.colors.auxiliar.warning : theme.colors.primary.main)};
  margin-bottom: 8px;
  &:last-child { margin-bottom: 0; }
`;

const FindingTitle = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FindingBody = styled.div`
  margin: 4px 0 6px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.4;
`;

const FindingFoot = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

/**
 * Bulgu paneli. Her bulgu: başlık + gövde (`${ns}.finding.<code>.title|body`, sayılar params'tan) + önerilen aksiyon düğmesi
 * (`${ns}.finding.<code>.action`). Aksiyonu çağıran yorumlar (listeyi süz, IDP daveti, inceleme kaydı…). Bulgu yoksa boş durum.
 */
export function FindingsPanel({
  ns, panel, span = 6, onAction, canAct = (f) => f.actionKind !== 'dataCompletion',
}) {
  const { t, i18n } = useTranslation();
  const findings = panel.data?.findings || [];
  // Sayılar dile göre biçimlenir (42,86); metin parametreleri sunucunun kural sonucundan — LLM yok.
  const paramsOf = (f) => ({
    ...Object.fromEntries(Object.entries(f.params || {}).map(([k, v]) => [k, typeof v === 'number' ? formatNum(v, i18n.language) : v])),
    n: formatNum(f.n, i18n.language, 0),
  });
  return (
    <PanelCard
      panelKey="findings" title={t(`${ns}.panel.findings`)} span={span}
      loading={panel.loading} error={panel.error} onRetry={panel.retry} empty={findings.length === 0}
      emptyText={t(`${ns}.finding.none`)}
    >
      <Note>{t(`${ns}.finding.disclaimer`)}</Note>
      {findings.map((f) => (
        <FindingCard key={f.code} $severity={f.severity} data-finding={f.code}>
          <FindingTitle>{t(`${ns}.finding.${f.code}.title`, paramsOf(f))}</FindingTitle>
          <FindingBody>{t(`${ns}.finding.${f.code}.body`, paramsOf(f))}</FindingBody>
          {f.actionKind && onAction && canAct(f) ? (
            <FindingFoot>
              <Button variant="outline" onClick={() => onAction(f)} data-action={`finding-${f.code}`}>
                {t(`${ns}.finding.${f.code}.action`)}
              </Button>
            </FindingFoot>
          ) : null}
        </FindingCard>
      ))}
    </PanelCard>
  );
}
